import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { EmojiEvents, Download, Close } from '@mui/icons-material';
import { trainingService, Certificate } from '../services/trainingService';

interface CourseCompletionModalProps {
  open: boolean;
  courseId: string;
  courseName: string;
  onClose: () => void;
}

export default function CourseCompletionModal({
  open,
  courseId,
  courseName,
  onClose,
}: CourseCompletionModalProps) {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && courseId) {
      generateCertificate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId]);

  const generateCertificate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const cert = await trainingService.generateCertificate(courseId);
      setCertificate(cert);
    } catch (err: any) {
      setError(err.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!certificate) return;

    setDownloading(true);
    setError(null);
    try {
      const data = await trainingService.downloadCertificate(certificate.id);
      if (data.pdfUrl) {
        window.open(data.pdfUrl, '_blank');
      } else {
        setError('Certificate PDF is being generated. Please try again in a moment.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          textAlign: 'center',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
          <EmojiEvents
            sx={{
              fontSize: 80,
              color: 'success.main',
            }}
          />
          <Typography variant="h4" fontWeight="bold" color="success.main">
            Congratulations!
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="h6" gutterBottom>
          You've completed
        </Typography>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
          {courseName}
        </Typography>

        {generating && (
          <Box py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Generating your certificate...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {certificate && !generating && (
          <Box mt={3}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your certificate has been generated!
            </Alert>

            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Certificate Number
              </Typography>
              <Typography variant="body1" fontWeight="bold" fontFamily="monospace">
                {certificate.certificateNumber}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Your certificate will be displayed on your caregiver profile and can be
              downloaded anytime from your training certificates page.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
        {certificate && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={downloading}
            size="large"
          >
            {downloading ? 'Downloading...' : 'Download Certificate'}
          </Button>
        )}
        <Button
          variant={certificate ? 'outlined' : 'contained'}
          startIcon={<Close />}
          onClick={onClose}
          size="large"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

